import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  PlusOutlined,
  ReconciliationOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasAnyPermission } from "../../utils/permissions";
import type {
  BankAccount,
  BankReconciliation,
  BankStatementBalance,
  CardSettlement,
  CashDeposit,
} from "../../types";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DATE = "YYYY-MM-DD";
const dateString = (value: Dayjs | string | null | undefined) =>
  value ? dayjs(value).format(DATE) : "";
const displayDate = (value: string | null | undefined) =>
  value ? dayjs(value).format("DD MMM YYYY") : "—";

/** Quick ranges so the document filter is a single click for common cases. */
const rangePresets: Array<{ label: string; value: [Dayjs, Dayjs] }> = [
  { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
  {
    label: "This month",
    value: [dayjs().startOf("month"), dayjs().endOf("month")],
  },
];

/** Idempotency key generated once per form opening, not once per click. */
const newRequestKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

const errorText = (error: any, fallback: string) =>
  error?.message || error?.error || fallback;

const statusTag = (status: CashDeposit["status"], lastError: string | null) => {
  if (status === "posted") return <Tag color="green">Posted</Tag>;
  if (status === "void") return <Tag>Void</Tag>;
  return (
    <Space direction="vertical" size={0}>
      <Tag color="orange">Pending posting</Tag>
      {lastError ? <Text type="secondary" style={{ fontSize: 11 }}>{lastError}</Text> : null}
    </Space>
  );
};

const BankingPage: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, "day"),
    dayjs(),
  ]);
  const [deposits, setDeposits] = useState<CashDeposit[]>([]);
  const [settlements, setSettlements] = useState<CardSettlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const [depositOpen, setDepositOpen] = useState(false);
  const [depositKey, setDepositKey] = useState("");
  const [depositSaving, setDepositSaving] = useState(false);
  const [depositForm] = Form.useForm();

  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementKey, setSettlementKey] = useState("");
  const [settlementSaving, setSettlementSaving] = useState(false);
  const [settlementForm] = Form.useForm();
  const settlementGross = Form.useWatch("gross_amount", settlementForm);
  const settlementFee = Form.useWatch("fee_amount", settlementForm);
  const settlementNet = useMemo(() => {
    const gross = Number(settlementGross || 0);
    const fee = Number(settlementFee || 0);
    return Math.round((gross - fee) * 100) / 100;
  }, [settlementGross, settlementFee]);

  const [clearTarget, setClearTarget] = useState<{
    kind: "cash" | "card";
    id: string;
    documentNo: string;
  } | null>(null);
  const [clearSaving, setClearSaving] = useState(false);
  const [clearForm] = Form.useForm();

  const [reconBankId, setReconBankId] = useState<string | undefined>(undefined);
  const [reconDate, setReconDate] = useState<Dayjs>(dayjs());
  const [recon, setRecon] = useState<BankReconciliation | null>(null);
  const [statementBalances, setStatementBalances] = useState<BankStatementBalance[]>([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState<string | null>(null);
  const [statementSaving, setStatementSaving] = useState(false);
  const [statementForm] = Form.useForm();

  const cashAccounts = useMemo(
    () => accounts.filter((a) => a.type === "cash" && a.is_active),
    [accounts],
  );
  const bankAccounts = useMemo(
    () => accounts.filter((a) => a.type === "bank" && a.is_active),
    [accounts],
  );
  const cardAccounts = useMemo(
    () => accounts.filter((a) => a.type === "card" && a.is_active),
    [accounts],
  );

  const canDeposit = hasAnyPermission("finance:accounts", "finance:close");
  const canSettle = hasAnyPermission("finance:accounts", "finance:close");
  const canReconcile = canDeposit;

  const loadAccounts = useCallback(async () => {
    try {
      const rows = await financeService.getBankAccounts(true);
      setAccounts(rows || []);
    } catch (error: any) {
      message.error(errorText(error, "Failed to load cash, bank and card accounts"));
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setDocumentsError(null);
    const params = {
      from: dateString(range[0]),
      to: dateString(range[1]),
      limit: 500,
    };
    try {
      const [depositRows, settlementRows] = await Promise.all([
        financeService.getCashDeposits(params),
        financeService.getCardSettlements(params),
      ]);
      setDeposits(depositRows || []);
      setSettlements(settlementRows || []);
    } catch (error: any) {
      setDocumentsError(errorText(error, "Failed to load banking documents"));
    } finally {
      setLoading(false);
    }
  }, [range]);

  const loadReconciliation = useCallback(async () => {
    if (!reconBankId) {
      setRecon(null);
      setStatementBalances([]);
      return;
    }
    setReconLoading(true);
    setReconError(null);
    try {
      const [result, balances] = await Promise.all([
        financeService.getBankReconciliation({
          bank_account_id: reconBankId,
          as_of: dateString(reconDate),
        }),
        financeService.getStatementBalances(reconBankId),
      ]);
      setRecon(result);
      setStatementBalances(balances || []);
    } catch (error: any) {
      setRecon(null);
      setReconError(errorText(error, "Failed to build the bank reconciliation"));
    } finally {
      setReconLoading(false);
    }
  }, [reconBankId, reconDate]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (reconBankId && bankAccounts.some((a) => a.id === reconBankId)) return;
    setReconBankId(bankAccounts[0]?.id);
  }, [bankAccounts, reconBankId]);

  useEffect(() => {
    loadReconciliation();
  }, [loadReconciliation]);

  useEffect(() => {
    statementForm.setFieldValue("statement_date", reconDate);
  }, [reconDate, statementForm]);

  const openDeposit = () => {
    setDepositKey(newRequestKey());
    setDepositOpen(true);
  };

  const openSettlement = () => {
    setSettlementKey(newRequestKey());
    setSettlementOpen(true);
  };

  const submitDeposit = async (values: any) => {
    setDepositSaving(true);
    try {
      const result = await financeService.createCashDeposit({
        business_date: dateString(values.business_date),
        cash_account_id: values.cash_account_id,
        bank_account_id: values.bank_account_id,
        amount: Number(values.amount),
        slip_reference: String(values.slip_reference || "").trim(),
        remarks: values.remarks ? String(values.remarks).trim() : undefined,
        idempotency_key: depositKey,
      });
      message.success(
        result?.already_posted
          ? `Deposit ${result.deposit.deposit_no} was already recorded`
          : `Deposit ${result?.deposit?.deposit_no || ""} posted to the bank`,
      );
      setDepositOpen(false);
      await loadDocuments();
      await loadReconciliation();
    } catch (error: any) {
      message.error(errorText(error, "Failed to post the cash deposit"));
    } finally {
      setDepositSaving(false);
    }
  };

  const submitSettlement = async (values: any) => {
    setSettlementSaving(true);
    try {
      const result = await financeService.createCardSettlement({
        business_date: dateString(values.business_date),
        card_account_id: values.card_account_id,
        bank_account_id: values.bank_account_id,
        gross_amount: Number(values.gross_amount),
        fee_amount: Number(values.fee_amount || 0),
        provider: values.provider ? String(values.provider).trim() : undefined,
        settlement_reference: String(values.settlement_reference || "").trim(),
        remarks: values.remarks ? String(values.remarks).trim() : undefined,
        idempotency_key: settlementKey,
      });
      message.success(
        result?.already_posted
          ? `Settlement ${result.settlement.settlement_no} was already recorded`
          : `Settlement ${result?.settlement?.settlement_no || ""} posted`,
      );
      setSettlementOpen(false);
      await loadDocuments();
      await loadReconciliation();
    } catch (error: any) {
      message.error(errorText(error, "Failed to post the card settlement"));
    } finally {
      setSettlementSaving(false);
    }
  };

  const retryDocument = async (kind: "cash" | "card", id: string) => {
    setRetryingId(id);
    try {
      if (kind === "cash") {
        const result = await financeService.retryCashDeposit(id);
        message.success(`Deposit ${result.deposit.deposit_no} posted`);
      } else {
        const result = await financeService.retryCardSettlement(id);
        message.success(`Settlement ${result.settlement.settlement_no} posted`);
      }
      await loadDocuments();
      await loadReconciliation();
    } catch (error: any) {
      message.error(errorText(error, "Failed to retry the pending posting"));
    } finally {
      setRetryingId(null);
    }
  };

  const submitClear = async (values: any) => {
    if (!clearTarget) return;
    setClearSaving(true);
    try {
      if (clearTarget.kind === "cash") {
        await financeService.clearCashDeposit(clearTarget.id, values.bank_reference);
      } else {
        await financeService.clearCardSettlement(clearTarget.id, values.bank_reference);
      }
      message.success(`${clearTarget.documentNo} marked cleared against the bank statement`);
      setClearTarget(null);
      clearForm.resetFields();
      await loadDocuments();
      await loadReconciliation();
    } catch (error: any) {
      message.error(errorText(error, "Failed to clear the document"));
    } finally {
      setClearSaving(false);
    }
  };

  const saveStatement = async (values: any) => {
    if (!reconBankId) return;
    setStatementSaving(true);
    try {
      await financeService.saveStatementBalance({
        bank_account_id: reconBankId,
        statement_date: dateString(values.statement_date),
        closing_balance: Number(values.closing_balance),
        statement_reference: values.statement_reference
          ? String(values.statement_reference).trim()
          : undefined,
        notes: values.notes ? String(values.notes).trim() : undefined,
      });
      message.success("Statement closing balance recorded");
      statementForm.setFieldsValue({ closing_balance: undefined, statement_reference: undefined, notes: undefined });
      await loadReconciliation();
    } catch (error: any) {
      message.error(errorText(error, "Failed to record the statement balance"));
    } finally {
      setStatementSaving(false);
    }
  };

  const openClear = (kind: "cash" | "card", id: string, documentNo: string) => {
    clearForm.resetFields();
    setClearTarget({ kind, id, documentNo });
  };

  const depositColumns: ColumnsType<CashDeposit> = [
    {
      title: "Date",
      dataIndex: "business_date",
      width: 105,
      render: (value: string) => displayDate(value),
    },
    { title: "Deposit no.", dataIndex: "deposit_no", width: 165 },
    {
      title: "From cash",
      key: "cash",
      width: 190,
      render: (_: any, row: CashDeposit) => (
        <Space direction="vertical" size={0}>
          <Text>{row.cash_account_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.cash_account_code}</Text>
        </Space>
      ),
    },
    {
      title: "To bank",
      key: "bank",
      width: 190,
      render: (_: any, row: CashDeposit) => (
        <Space direction="vertical" size={0}>
          <Text>{row.bank_account_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.bank_account_code}</Text>
        </Space>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      width: 130,
      render: (value: number) => <Text strong>{formatLkr(value)}</Text>,
    },
    {
      title: "Deposit slip",
      dataIndex: "slip_reference",
      width: 180,
      render: (value: string, row: CashDeposit) => (
        <Space direction="vertical" size={0}>
          <Text>{value}</Text>
          {row.remarks ? (
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: row.remarks }}>
              {row.remarks}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 145,
      render: (status: CashDeposit["status"], row: CashDeposit) =>
        statusTag(status, row.last_error),
    },
    {
      title: "Bank match",
      key: "cleared",
      width: 190,
      render: (_: any, row: CashDeposit) =>
        row.cleared_at ? (
          <Space direction="vertical" size={0}>
            <Tag icon={<CheckCircleOutlined />} color="green">Cleared</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.bank_reference || "Bank reference missing"}
            </Text>
          </Space>
        ) : (
          <Tag>Not cleared</Tag>
        ),
    },
    {
      title: "Recorded",
      dataIndex: "created_at",
      width: 150,
      render: (value: string) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Entered by",
      dataIndex: "created_by_name",
      width: 140,
      render: (value: string | null) => value || "—",
    },
    {
      title: "",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_: any, row: CashDeposit) => {
        if (!canReconcile) return null;
        if (row.status === "pending") {
          return (
            <Button
              size="small"
              loading={retryingId === row.id}
              onClick={() => retryDocument("cash", row.id)}
            >
              Retry posting
            </Button>
          );
        }
        if (row.status === "posted" && !row.cleared_at) {
          return (
            <Button
              size="small"
              icon={<ReconciliationOutlined />}
              onClick={() => openClear("cash", row.id, row.deposit_no)}
            >
              Match bank
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const settlementColumns: ColumnsType<CardSettlement> = [
    {
      title: "Date",
      dataIndex: "business_date",
      width: 105,
      render: (value: string) => displayDate(value),
    },
    { title: "Settlement no.", dataIndex: "settlement_no", width: 170 },
    {
      title: "Card clearing",
      key: "card",
      width: 175,
      render: (_: any, row: CardSettlement) => (
        <Space direction="vertical" size={0}>
          <Text>{row.card_account_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.card_account_code}</Text>
        </Space>
      ),
    },
    {
      title: "Bank",
      key: "bank",
      width: 175,
      render: (_: any, row: CardSettlement) => (
        <Space direction="vertical" size={0}>
          <Text>{row.bank_account_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.bank_account_code}</Text>
        </Space>
      ),
    },
    {
      title: "Gross",
      dataIndex: "gross_amount",
      align: "right",
      width: 120,
      render: (value: number) => formatLkr(value),
    },
    {
      title: "Fee",
      dataIndex: "fee_amount",
      align: "right",
      width: 105,
      render: (value: number) => formatLkr(value),
    },
    {
      title: "Net to bank",
      dataIndex: "net_amount",
      align: "right",
      width: 125,
      render: (value: number) => <Text strong>{formatLkr(value)}</Text>,
    },
    {
      title: "Reference",
      dataIndex: "settlement_reference",
      width: 175,
      render: (value: string, row: CardSettlement) => (
        <Space direction="vertical" size={0}>
          <Text>{value}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {row.provider || "No processor"}
          </Text>
          {row.remarks ? (
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: row.remarks }}>
              {row.remarks}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 135,
      render: (status: CashDeposit["status"], row: CardSettlement) =>
        statusTag(status, row.last_error),
    },
    {
      title: "Bank match",
      key: "cleared",
      width: 175,
      render: (_: any, row: CardSettlement) =>
        row.cleared_at ? (
          <Space direction="vertical" size={0}>
            <Tag icon={<CheckCircleOutlined />} color="green">Cleared</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.bank_reference || "Bank reference missing"}
            </Text>
          </Space>
        ) : (
          <Tag>Not cleared</Tag>
        ),
    },
    {
      title: "Recorded",
      dataIndex: "created_at",
      width: 150,
      render: (value: string) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_: any, row: CardSettlement) => {
        if (!canReconcile) return null;
        if (row.status === "pending") {
          return (
            <Button
              size="small"
              loading={retryingId === row.id}
              onClick={() => retryDocument("card", row.id)}
            >
              Retry posting
            </Button>
          );
        }
        if (row.status === "posted" && !row.cleared_at) {
          return (
            <Button
              size="small"
              icon={<ReconciliationOutlined />}
              onClick={() => openClear("card", row.id, row.settlement_no)}
            >
              Match bank
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const statementColumns: ColumnsType<BankStatementBalance> = [
    {
      title: "Statement date",
      dataIndex: "statement_date",
      render: (value: string) => displayDate(value),
    },
    {
      title: "Closing balance",
      dataIndex: "closing_balance",
      align: "right",
      render: (value: number) => formatLkr(value),
    },
    { title: "Reference", dataIndex: "statement_reference", render: (v: string | null) => v || "—" },
    { title: "Notes", dataIndex: "notes", render: (v: string | null) => v || "—" },
    { title: "Recorded by", dataIndex: "created_by_name", render: (v: string | null) => v || "—" },
    {
      title: "Recorded at",
      dataIndex: "created_at",
      render: (v: string) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
    },
  ];

  const outstandingDepositColumns: ColumnsType<CashDeposit> = [
    { title: "Date", dataIndex: "business_date", render: (v: string) => displayDate(v) },
    { title: "Deposit no.", dataIndex: "deposit_no" },
    { title: "Slip", dataIndex: "slip_reference" },
    { title: "Amount", dataIndex: "amount", align: "right", render: (v: number) => formatLkr(v) },
    {
      title: "",
      key: "match",
      render: (_: any, row: CashDeposit) =>
        canReconcile ? (
          <Button size="small" onClick={() => openClear("cash", row.id, row.deposit_no)}>
            Match bank
          </Button>
        ) : null,
    },
  ];

  const outstandingSettlementColumns: ColumnsType<CardSettlement> = [
    { title: "Date", dataIndex: "business_date", render: (v: string) => displayDate(v) },
    { title: "Settlement no.", dataIndex: "settlement_no" },
    { title: "Reference", dataIndex: "settlement_reference" },
    { title: "Net", dataIndex: "net_amount", align: "right", render: (v: number) => formatLkr(v) },
    {
      title: "",
      key: "match",
      render: (_: any, row: CardSettlement) =>
        canReconcile ? (
          <Button size="small" onClick={() => openClear("card", row.id, row.settlement_no)}>
            Match bank
          </Button>
        ) : null,
    },
  ];

  const cashTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="A cash deposit is a transfer, not extra income"
        description="Posting a deposit credits cash-in-hand and debits the destination bank. Only the bank balance changes side; the P&L is never touched."
      />
      {cashAccounts.length === 0 || bankAccounts.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Cash or bank account setup is incomplete"
          description="Configure at least one active cash account and one active bank account in Finance Setup."
        />
      ) : null}
      <Card
        title="Cash deposits to bank"
        extra={
          canDeposit ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openDeposit}>
              Record cash deposit
            </Button>
          ) : null
        }
      >
        {documentsError ? <Alert type="error" showIcon message={documentsError} style={{ marginBottom: 16 }} /> : null}
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={deposits}
          columns={depositColumns}
          pagination={{ pageSize: 12, showSizeChanger: false }}
          scroll={{ x: 1800 }}
          locale={{ emptyText: <Empty description="No cash deposits in this date range" /> }}
        />
      </Card>
    </Space>
  );

  const cardTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Card receipts clear through card clearing, never through cash"
        description="Settlement debits the bank for the net amount, debits Card Processing Fees (6710) for the provider fee, and credits Card Clearing for the gross customer payments."
      />
      {cardAccounts.length === 0 || bankAccounts.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Card clearing or bank account setup is incomplete"
          description="Configure at least one active card clearing account and one active bank account in Finance Setup."
        />
      ) : null}
      <Card
        title="Card settlements"
        extra={
          canSettle ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openSettlement}>
              Record card settlement
            </Button>
          ) : null
        }
      >
        {documentsError ? <Alert type="error" showIcon message={documentsError} style={{ marginBottom: 16 }} /> : null}
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={settlements}
          columns={settlementColumns}
          pagination={{ pageSize: 12, showSizeChanger: false }}
          scroll={{ x: 1850 }}
          locale={{ emptyText: <Empty description="No card settlements in this date range" /> }}
        />
      </Card>
    </Space>
  );

  const reconciliationTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Card>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={12} lg={10}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Bank account
            </Text>
            <Select
              style={{ width: "100%" }}
              value={reconBankId}
              onChange={setReconBankId}
              placeholder="Choose a bank account"
              options={bankAccounts.map((account) => ({
                value: account.id,
                label: `${account.name}${account.account_no ? ` · ${account.account_no}` : ""}`,
              }))}
            />
          </Col>
          <Col xs={24} md={8} lg={7}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Statement as at
            </Text>
            <DatePicker
              style={{ width: "100%" }}
              value={reconDate}
              allowClear={false}
              onChange={(value) => value && setReconDate(value)}
            />
          </Col>
          <Col xs={24} md={4} lg={4}>
            <Button
              block
              icon={<ReconciliationOutlined />}
              onClick={loadReconciliation}
              loading={reconLoading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {reconError ? <Alert type="error" showIcon message={reconError} /> : null}
      {!reconBankId ? (
        <Card><Empty description="Configure a bank account to reconcile" /></Card>
      ) : recon ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={6}>
              <Card><Statistic title="Book balance (GL)" value={formatLkr(recon.book_balance)} prefix={<WalletOutlined />} /></Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Statement closing balance"
                  value={recon.statement_balance === null ? "—" : formatLkr(recon.statement_balance)}
                  prefix={<BankOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card><Statistic title="Outstanding deposits / settlements" value={formatLkr(recon.outstanding_total)} prefix={<CreditCardOutlined />} /></Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Variance"
                  value={recon.variance === null ? "—" : formatLkr(recon.variance)}
                  valueStyle={{
                    color:
                      recon.variance === null
                        ? undefined
                        : Math.abs(recon.variance) < 0.005
                          ? "#389e0d"
                          : "#cf1322",
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Alert
            type={recon.variance !== null && Math.abs(recon.variance) >= 0.005 ? "warning" : "success"}
            showIcon
            message={
              recon.variance === null
                ? "No statement balance recorded for this date"
                : Math.abs(recon.variance) < 0.005
                  ? "Book and statement agree after outstanding items"
                  : `Variance ${formatLkr(recon.variance)} needs investigation`
            }
            description={
              recon.statement_balance === null
                ? `Book balance ${formatLkr(recon.book_balance)} less outstanding deposits ${formatLkr(recon.outstanding_deposits)} and card settlements ${formatLkr(recon.outstanding_settlements)} gives expected statement balance ${formatLkr(recon.expected_statement_balance)}. Record the bank statement closing balance to compare.`
                : `Expected statement balance = book balance ${formatLkr(recon.book_balance)} − outstanding ${formatLkr(recon.outstanding_total)} = ${formatLkr(recon.expected_statement_balance)}.`
            }
          />

          <Card title="Record bank statement closing balance">
            <Form
              form={statementForm}
              layout="vertical"
              onFinish={saveStatement}
              initialValues={{ statement_date: dayjs() }}
            >
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12} lg={6}>
                  <Form.Item name="statement_date" label="Statement date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: "100%" }} allowClear={false} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Form.Item
                    name="closing_balance"
                    label="Closing balance"
                    rules={[{ required: true, message: "Enter the statement closing balance" }]}
                  >
                    <InputNumber style={{ width: "100%" }} precision={2} placeholder="0.00" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Form.Item name="statement_reference" label="Reference">
                    <Input placeholder="Statement / page" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Form.Item name="notes" label="Notes">
                    <Input placeholder="Optional" />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end">
                <Col>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={statementSaving} disabled={!canReconcile}>
                      Save statement
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card title={`Outstanding cash deposits (${recon.outstanding_cash_deposits.length})`}>
            <Table
              size="small"
              rowKey="id"
              dataSource={recon.outstanding_cash_deposits}
              columns={outstandingDepositColumns}
              pagination={false}
              locale={{ emptyText: <Empty description="No uncleared cash deposits" /> }}
            />
          </Card>

          <Card title={`Outstanding card settlements (${recon.outstanding_card_settlements.length})`}>
            <Table
              size="small"
              rowKey="id"
              dataSource={recon.outstanding_card_settlements}
              columns={outstandingSettlementColumns}
              pagination={false}
              locale={{ emptyText: <Empty description="No uncleared card settlements" /> }}
            />
          </Card>

          <Card title="Statement balance history">
            <Table
              size="small"
              rowKey="id"
              dataSource={statementBalances}
              columns={statementColumns}
              pagination={{ pageSize: 6, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="No statement balances recorded yet" /> }}
            />
          </Card>
        </>
      ) : (
        <Card loading={reconLoading}><Empty description="No reconciliation loaded" /></Card>
      )}
    </Space>
  );

  return (
    <div style={{ maxWidth: 1500, margin: "0 auto" }}>
      <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Banking &amp; Cash Control</Title>
        <Text type="secondary">
          Deposit cash into the bank, settle card receipts, and prove the bank statement against the ledger.
        </Text>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={16} lg={14}>
            <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
              Document date range
            </Text>
            <RangePicker
              style={{ width: "100%" }}
              value={range}
              allowClear={false}
              presets={rangePresets}
              onChange={(values) => {
                if (values?.[0] && values?.[1]) setRange([values[0], values[1]]);
              }}
            />
          </Col>
          <Col xs={24} md={8} lg={6}>
            <Button block onClick={loadDocuments} loading={loading}>
              Refresh documents
            </Button>
          </Col>
        </Row>
      </Card>

      <Tabs
        items={[
          {
            key: "cash",
            label: <Space><WalletOutlined />Cash deposits</Space>,
            children: cashTab,
          },
          {
            key: "card",
            label: <Space><CreditCardOutlined />Card settlements</Space>,
            children: cardTab,
          },
          {
            key: "reconciliation",
            label: <Space><ReconciliationOutlined />Bank reconciliation</Space>,
            children: reconciliationTab,
            forceRender: true,
          },
        ]}
      />

      <Modal
        title="Record cash deposit to bank"
        open={depositOpen}
        onCancel={() => setDepositOpen(false)}
        onOk={() => depositForm.submit()}
        afterOpenChange={(open) => {
          if (!open) return;
          depositForm.resetFields();
          depositForm.setFieldsValue({ business_date: dayjs() });
        }}
        confirmLoading={depositSaving}
        okText="Post deposit"
        destroyOnHidden
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="This posts Dr bank / Cr cash-in-hand. No income account is used."
        />
        <Form form={depositForm} layout="vertical" onFinish={submitDeposit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="business_date" label="Business date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount deposited"
                rules={[{ required: true, message: "Enter the amount" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0.01} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cash_account_id" label="Source cash account" rules={[{ required: true }]}>
                <Select
                  placeholder="Cash-in-hand"
                  options={cashAccounts.map((account) => ({
                    value: account.id,
                    label: `${account.name} (${account.account_code || "—"})`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bank_account_id" label="Destination bank account" rules={[{ required: true }]}>
                <Select
                  placeholder="Bank account"
                  options={bankAccounts.map((account) => ({
                    value: account.id,
                    label: `${account.name}${account.account_no ? ` · ${account.account_no}` : ""}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="slip_reference"
            label="Deposit slip / bank reference"
            rules={[{ required: true, message: "A deposit slip reference is required" }]}
          >
            <Input placeholder="e.g. SLIP-2026-00124" maxLength={100} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="Optional note for the cashier / accountant" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Record card settlement"
        open={settlementOpen}
        onCancel={() => setSettlementOpen(false)}
        onOk={() => settlementForm.submit()}
        afterOpenChange={(open) => {
          if (!open) return;
          settlementForm.resetFields();
          settlementForm.setFieldsValue({
            business_date: dayjs(),
            fee_amount: 0,
          });
        }}
        confirmLoading={settlementSaving}
        okText="Post settlement"
        destroyOnHidden
        width={700}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The bank receives the net amount. The provider fee is an expense, not a cash movement."
        />
        <Form form={settlementForm} layout="vertical" onFinish={submitSettlement}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="business_date" label="Business date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} allowClear={false} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="card_account_id" label="Card clearing account" rules={[{ required: true }]}>
                <Select
                  placeholder="Card clearing"
                  options={cardAccounts.map((account) => ({
                    value: account.id,
                    label: `${account.name} (${account.account_code || "—"})`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bank_account_id" label="Bank account" rules={[{ required: true }]}>
                <Select
                  placeholder="Destination bank"
                  options={bankAccounts.map((account) => ({
                    value: account.id,
                    label: account.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="gross_amount"
                label="Gross card receipts"
                rules={[{ required: true, message: "Enter gross receipts" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0.01} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="fee_amount"
                label="Processor fee"
                dependencies={["gross_amount"]}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator: (_rule, value) => {
                      const gross = Number(getFieldValue("gross_amount") || 0);
                      if (!gross) return Promise.resolve();
                      return Number(value || 0) < gross
                        ? Promise.resolve()
                        : Promise.reject(new Error("Fee must be less than gross receipts"));
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Net to bank">
                <InputNumber style={{ width: "100%" }} value={settlementNet} readOnly precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="provider" label="Card processor">
                <Input placeholder="e.g. Visa / Mastercard acquirer" maxLength={120} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="settlement_reference"
                label="Settlement reference"
                rules={[{ required: true, message: "A settlement reference is required" }]}
              >
                <Input placeholder="Acquirer settlement / batch reference" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Match bank statement"
        open={!!clearTarget}
        onCancel={() => {
          setClearTarget(null);
          clearForm.resetFields();
        }}
        onOk={() => clearForm.submit()}
        afterOpenChange={(open) => {
          if (open) clearForm.resetFields();
        }}
        confirmLoading={clearSaving}
        okText="Mark cleared"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message={`Marking ${clearTarget?.documentNo || "this document"} as cleared`}
          description="Enter the bank statement reference that proves the money reached the bank. The document remains in the audit history."
          style={{ marginBottom: 16 }}
        />
        <Form form={clearForm} layout="vertical" onFinish={submitClear}>
          <Form.Item
            name="bank_reference"
            label="Bank statement reference"
            rules={[{ required: true, message: "Enter the bank statement reference" }]}
          >
            <Input placeholder="Statement line / transaction reference" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BankingPage;
